import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeartbeat, FaGavel, FaChartLine } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import TabNav from '../components/TabNav';
import ScrollReveal from '../components/ScrollReveal';
import { FIXED_H } from '../components/layoutConstants';
import imgHistoire from '../images/Règles/badmintonHistory.png';
import imgPrincipes from '../images/Règles/BadmintonRules.png';
import imgFrappes from '../images/Règles/BadmintonFrappe.png';

const tabDefs = [
  { key: 'histoire',   label: 'Histoire',    eyebrow: 'Les origines',            title: 'Histoire du Badminton'    },
  { key: 'principes',  label: 'Principes',   eyebrow: 'Bases du jeu',            title: 'Principes de Base'        },
  { key: 'regles',     label: 'Règles',      eyebrow: 'À connaître',             title: 'Règles Importantes'       },
  { key: 'frappes',    label: 'Frappes',     eyebrow: 'Technique',               title: 'Principes de Frappe'      },
  { key: 'classement', label: 'Classement',  eyebrow: 'Niveaux & compétition',   title: 'Classement & Compétition' },
  { key: 'bienfaits',  label: 'Bienfaits',   eyebrow: 'Santé & bien-être',       title: 'Bienfaits du Badminton'   },
];

const Img = ({ src, alt }) => (
  <div className="rounded-2xl overflow-hidden aspect-[16/7] mb-8">
    <img src={src} alt={alt} className="w-full h-full object-cover" />
  </div>
);

const List = ({ items, ordered }) => (
  <ul className="space-y-2.5">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-sm text-stone-600 leading-relaxed">
        {ordered
          ? <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-800 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
          : <span className="w-1.5 h-1.5 rounded-full bg-brand-800 shrink-0 mt-2" />
        }
        {typeof item === 'string' ? item : <span><strong className="text-stone-800">{item.label} :</strong> {item.text}</span>}
      </li>
    ))}
  </ul>
);

const Regles = ({ setCurrentSection, currentSection }) => {
  const { tab }     = useParams();
  const navigate    = useNavigate();
  const stickyRef   = useRef(null);
  const sectionRefs = useRef({});
  const activeRef   = useRef(null);
  const isProg      = useRef(false);
  const [active, setActive] = useState(tab || 'histoire');

  const scrollOffset = useCallback(() => FIXED_H + (stickyRef.current?.offsetHeight ?? 0), []);

  const goTo = useCallback((key, behavior = 'smooth') => {
    const el = sectionRefs.current[key];
    if (!el) return;
    isProg.current = true;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - scrollOffset(), behavior });
    clearTimeout(goTo._t);
    goTo._t = setTimeout(() => { isProg.current = false; }, 900);
    setActive(key);
    navigate(`/regles/${key}`, { replace: true });
  }, [navigate, scrollOffset]);

  useEffect(() => {
    const key = tab || 'histoire';
    if (key !== 'histoire') { const t = setTimeout(() => goTo(key, 'instant'), 120); return () => clearTimeout(t); }
  }, []); // eslint-disable-line

  useEffect(() => {
    const onScroll = () => {
      if (isProg.current) return;
      const threshold = scrollOffset() + 40;
      let current = tabDefs[0].key;
      for (const { key } of tabDefs) {
        const el = sectionRefs.current[key];
        if (el && el.getBoundingClientRect().top <= threshold) current = key;
      }
      if (current !== activeRef.current) { activeRef.current = current; setActive(current); navigate(`/regles/${current}`, { replace: true }); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [navigate, scrollOffset]);

  const ref = (key) => (el) => { sectionRefs.current[key] = el; };
  const meta = tabDefs.find(t => t.key === active);

  return (
    <>
      <PageHeader title="Règles du Badminton" subtitle="Histoire, règles et techniques du sport le plus rapide au monde"
        setCurrentSection={setCurrentSection} currentSection={currentSection} />

      <div ref={stickyRef} className="sticky z-30 bg-white/95 backdrop-blur-md border-b border-stone-100 shadow-[0_1px_0_rgba(0,0,0,0.04)]" style={{ top: FIXED_H }}>
        <div className="max-w-6xl mx-auto px-5 md:pl-8 py-3 flex items-center gap-3 min-h-[52px]">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }} className="flex items-center gap-3">
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-brand-800/60">{meta?.eyebrow}</span>
              <div className="w-px h-4 bg-stone-200 shrink-0" />
              <span className="text-sm font-bold text-stone-950">{meta?.title}</span>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="border-t border-stone-100">
          <div className="max-w-6xl mx-auto px-5 md:pl-8">
            <TabNav tabs={tabDefs} activeTab={active} onTabChange={goTo} />
          </div>
        </div>
      </div>

      {/* Histoire */}
      <div ref={ref('histoire')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">Les origines</span><h2 className="display-md">Histoire du Badminton</h2></ScrollReveal>
            <div className="max-w-2xl">
              <Img src={imgHistoire} alt="Histoire du Badminton" />
              <p className="text-sm text-stone-600 leading-relaxed mb-3">Inventé au 19e siècle au Royaume-Uni, le badminton est un héritage du "poona", un jeu de raquette indien. Il est devenu discipline olympique en 1992.</p>
              <p className="text-sm text-stone-600 leading-relaxed">Le record de vitesse en compétition est de <strong>426 km/h</strong> (Mads Pieler Kolding, 2017). En conditions contrôlées, 565 km/h !</p>
            </div>
          </div>
        </section>
      </div>

      {/* Principes */}
      <div ref={ref('principes')}>
        <section className="section bg-white">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">Bases du jeu</span><h2 className="display-md">Principes de Base</h2></ScrollReveal>
            <div className="max-w-2xl">
              <Img src={imgPrincipes} alt="Principes de base" />
              <List ordered items={[
                'Le badminton se joue en simple (1v1) ou en double (2v2).',
                'Le service alterne entre la droite (score pair) et la gauche (score impair).',
                'On utilise une raquette et un volant de 16 plumes avec bouchon en liège.',
                'Un tirage au sort (toss) détermine qui sert en premier.',
                "Le service s'effectue du bas vers le haut, sous la ceinture.",
              ]} />
            </div>
          </div>
        </section>
      </div>

      {/* Règles */}
      <div ref={ref('regles')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">À connaître</span><h2 className="display-md">Règles Importantes</h2></ScrollReveal>
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-6"><FaGavel className="text-brand-800" /><p className="text-sm font-semibold text-stone-700">Règles officielles FFBaD</p></div>
              <List items={[
                "Le filet fait partie du jeu. Un volant qui le touche reste en jeu s'il passe de l'autre côté.",
                "Les pieds du serveur ne doivent pas toucher les lignes au moment du service.",
                "Faute si le volant touche le sol hors limites ou est frappé deux fois de suite.",
                "Il est interdit de distraire ou de provoquer l'adversaire.",
                "Un match se joue en 2 sets gagnants de 21 points (2 points d'écart, max 30).",
              ]} />
            </div>
          </div>
        </section>
      </div>

      {/* Frappes */}
      <div ref={ref('frappes')}>
        <section className="section bg-white">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">Technique</span><h2 className="display-md">Principes de Frappe</h2></ScrollReveal>
            <div className="max-w-2xl">
              <Img src={imgFrappes} alt="Principes de frappe" />
              <List items={[
                { label: 'Dégagement défensif', text: "Frappe haute et longue pour repousser l'adversaire au fond." },
                { label: 'Dégagement offensif', text: "Frappe puissante et rapide mettant l'adversaire en difficulté." },
                { label: 'Drive',               text: 'Frappe à mi-hauteur, rapide et tendue, parallèle au filet.' },
                { label: 'Smash',               text: 'Frappe offensive du haut vers le bas, pour marquer le point.' },
                { label: 'Amorti',              text: 'Frappe douce et précise juste derrière le filet.' },
                { label: 'Contre-amorti',       text: 'Réponse courte à un amorti, maintenant le volant près du filet.' },
              ]} />
            </div>
          </div>
        </section>
      </div>

      {/* Classement */}
      <div ref={ref('classement')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">Niveaux & compétition</span><h2 className="display-md">Classement & Compétition</h2></ScrollReveal>
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4"><FaChartLine className="text-brand-800" /></div>
              <p className="text-sm text-stone-600 leading-relaxed mb-3">La FFBaD utilise un système de points : NC → P (Promotion) → D (Départemental) → R (Régional) → N (National).</p>
              <p className="text-sm text-stone-600 leading-relaxed">Le classement est calculé sur les 6 meilleurs résultats des 12 derniers mois.</p>
            </div>
          </div>
        </section>
      </div>

      {/* Bienfaits */}
      <div ref={ref('bienfaits')}>
        <section className="section bg-white">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">Santé & bien-être</span><h2 className="display-md">Bienfaits du Badminton</h2></ScrollReveal>
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4"><FaHeartbeat className="text-brand-800" /></div>
              <List items={[
                'Environ 500 kcal dépensées en une heure.',
                "Améliore l'endurance, la coordination et les réflexes.",
                'Raffermit cuisses, fessiers et mollets.',
                'Booste le moral en libérant des endorphines.',
              ]} />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Regles;
